import type { ReactNode } from "react";

interface InkButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: "ink" | "sindoor" | "ghost";
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}

const VARIANTS: Record<NonNullable<InkButtonProps["variant"]>, string> = {
  // Solid ink — the default, weighted action.
  ink: "bg-ink text-paper border-ink hover:bg-ink-soft",
  // Sindoor — rubrication. Reserved for the one ritual action on a screen.
  sindoor: "bg-sindoor text-paper border-sindoor hover:opacity-90",
  // Ghost — ink outline on paper; hover rubricates (ink → sindoor).
  ghost:
    "bg-transparent text-ink border-rule hover:border-sindoor hover:text-sindoor",
};

/**
 * InkButton — a flat, lettered action.
 *
 * No gradient, no glow: a rectangle of ink (or sindoor, or an ink
 * hairline). The `sindoor` variant is rubrication — used sparingly,
 * for the single ritual action. Chrome labels are English-led (a learner
 * who cannot yet read Devanagari must be able to operate every button) —
 * pass a `DualLabel` (or plain English text) as `children`. This still
 * uses `font-dev` as its base family only so any Devanagari CONTENT that
 * legitimately appears inside a button (rare) renders correctly; it never
 * applies letter-spacing — never letter-space Devanagari, it breaks
 * conjunct shaping (e.g. न्द, र्ण).
 */
export function InkButton({
  children,
  onClick,
  variant = "ink",
  disabled = false,
  type = "button",
  className = "",
}: InkButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center border px-6 py-2.5 font-latin text-base transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40 ${VARIANTS[variant]} ${className}`}
    >
      {children}
    </button>
  );
}
